/**
 * Custom Function Registry
 * Allows applications to register custom functions for use in expressions
 */

import type { ExpressionFunction } from './ast.js';

/**
 * Global custom function registry
 */
const customFunctions = new Map<string, ExpressionFunction>();

/**
 * Register a custom function for use in expressions
 * @param name Function name (must be a valid identifier)
 * @param fn Function implementation
 */
export function registerCustomFunction(
  name: string,
  fn: ExpressionFunction,
): void {
  if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)) {
    throw new Error(
      `Invalid function name: ${name}. Must be a valid JavaScript identifier.`,
    );
  }
  customFunctions.set(name, fn);
}

/**
 * Unregister a custom function
 * @param name Function name to remove
 */
export function unregisterCustomFunction(name: string): void {
  customFunctions.delete(name);
}

/**
 * Get a custom function by name
 * @param name Function name
 * @returns Function implementation or undefined
 */
export function getCustomFunction(
  name: string,
): ExpressionFunction | undefined {
  return customFunctions.get(name);
}

/**
 * Get all registered custom functions
 * @returns Record of all custom functions
 */
export function getAllCustomFunctions(): Record<string, ExpressionFunction> {
  return Object.fromEntries(customFunctions.entries());
}

/**
 * Clear all custom functions
 */
export function clearCustomFunctions(): void {
  customFunctions.clear();
}

/**
 * Check if a function is registered
 * @param name Function name
 * @returns true if function is registered
 */
export function hasCustomFunction(name: string): boolean {
  return customFunctions.has(name);
}
